import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '../../../Components/TanstackTable'
import useReportTable from '../useReportTable'
import { AgentRevenueReportProps, RevenueData } from './types'
import { formatNumber, getRevenueOveralls } from './utils'

export const AgentRevenueReportTable = (props: AgentRevenueReportProps) => {
    const { reportsData, isLoading } = props
    const { t } = useTranslation()

    const reports = useMemo(() => reportsData?.data || [], [reportsData])
    const months = useMemo(() => reportsData?.timePeriod || [], [reportsData])

    const columns = useMemo<ColumnDef<RevenueData>[]>(() => {
        const agentColumn: ColumnDef<RevenueData> = {
            id: 'agent',
            header: t('app.common.agent'),
            enableSorting: false,
            accessorFn: (row) => `${row.agent?.firstName} ${row.agent?.lastName}`,
        }

        const monthColumns: ColumnDef<RevenueData>[] = months?.map(({ month, monthName, year }) => ({
            id: `${month}/${year}`,
            header: monthName,
            enableSorting: false,
            cell: (info) => {
                const revenue = info.row.original.revenueTotals?.find((rev) => rev?.month === month)
                if (!revenue) return null
                return (
                    <div className="d-flex gap-2 justify-content-center">
                        {revenue.totalSale != null && <span>{formatNumber(revenue.totalSale)}</span>}
                        {revenue.totalRent != null && <span className="text-danger">{formatNumber(revenue.totalRent)}</span>}
                    </div>
                )
            },
        }))

        const totalColumn: ColumnDef<RevenueData> = {
            id: 'total_by_agent',
            header: t('app.common.total'),
            enableSorting: false,
            cell: (info) => <div className="fw-bolder text-center">{formatNumber(info.row.original.agentGrandTotal || 0)}</div>,
        }

        return [agentColumn, ...monthColumns, totalColumn]
    }, [months, t])

    const tableFooter = useMemo(() => {
        const { monthOveralls, grandOverall } = getRevenueOveralls(reports, months)

        return (
            <tr>
                <td>{t('app.common.overall')}</td>
                {monthOveralls.map((overall, i) => (
                    <td key={i} className="text-center">
                        {formatNumber(overall.count)}
                    </td>
                ))}
                <td className="fw-bolder text-center">{formatNumber(grandOverall)}</td>
            </tr>
        )
    }, [reports, months, t])

    const table = useReportTable(columns, reports)

    return (
        <DataTable
            table={table}
            tableFooter={tableFooter}
            isLoading={isLoading}
            theadClass="dmn-table-header"
        />
    )
}
